import React, { useMemo, useRef, useState } from "react";
import {
  Popover,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import PublicOutlinedIcon from "@material-ui/icons/PublicOutlined";
import ExpandMoreRoundedIcon from "@material-ui/icons/ExpandMoreRounded";
import CheckRoundedIcon from "@material-ui/icons/CheckRounded";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import { TIMEZONE_OPTIONS } from "../../Context/TimezoneContext";
// Real SVG flags, not emoji - Windows (Chrome/Edge included) has no built-in
// color-flag font and renders flag emoji as plain two-letter text, which is
// exactly what this replaced. Self-hosted (no CDN calls at runtime): the CSS
// below references SVGs bundled in this package, not an external service.
import "flag-icons/css/flag-icons.min.css";

// `.fi` (flag-icons' base class) + `.fis` (square crop, better suited to a
// small rounded chip than the default 4x3 rectangle) + `.fi-{cc}`.
const flagClass = (countryCode) => `fi fis fi-${(countryCode || "xx").toLowerCase()}`;

const useStyles = makeStyles((theme) => ({
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#ffffff",
    fontSize: "0.82rem",
    fontWeight: 700,
    background: "rgba(255, 255, 255, 0.18)",
    border: "1px solid rgba(255, 255, 255, 0.28)",
    borderRadius: 10,
    padding: "0 12px",
    height: 36,
    boxSizing: "border-box",
    cursor: "pointer",
    lineHeight: 1,
    whiteSpace: "nowrap",
    backdropFilter: "blur(6px)",
    transition: "all 0.15s ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.28)",
      borderColor: "rgba(255, 255, 255, 0.45)",
      transform: "translateY(-1px)",
    },
    [theme.breakpoints.down("xs")]: {
      height: 34,
      padding: "0 8px",
      fontSize: "0.75rem",
      gap: 5,
    },
  },
  triggerOpen: {
    background: "rgba(255, 255, 255, 0.28)",
    borderColor: "rgba(255, 255, 255, 0.45)",
  },
  triggerFlag: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    flexShrink: 0,
    boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.6)",
  },
  triggerChevron: {
    fontSize: "1.1rem",
    opacity: 0.9,
    transition: "transform 0.15s ease",
  },
  triggerChevronOpen: {
    transform: "rotate(180deg)",
  },
  paper: {
    width: "min(92vw, 320px)",
    maxHeight: "min(75vh, 420px)",
    display: "flex",
    flexDirection: "column",
    borderRadius: 14,
    overflow: "hidden",
    // Explicit, opaque background - without it the Popover's Paper picked up
    // a translucent one (from this app's own global CSS resetting Paper's
    // default), and the dashboard behind it showed through the list.
    backgroundColor: "#ffffff",
    boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.08)",
    border: "1px solid #e2e8f0",
  },
  searchBar: {
    // Fixed on top of the list, per the ask - the list below scrolls
    // independently, this never moves.
    flexShrink: 0,
    padding: "10px 12px 8px",
    borderBottom: "1px solid #eef1f5",
    background: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  searchLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginBottom: 6,
  },
  list: {
    overflowY: "auto",
    flex: 1,
    padding: "6px 0",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    cursor: "pointer",
    transition: "background 0.1s ease",
    "&:hover": {
      background: "#f8fafc",
    },
  },
  optionActive: {
    background: "#fff7ed",
    "&:hover": {
      background: "#fff7ed",
    },
  },
  optionFlag: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    flexShrink: 0,
    boxShadow: "0 0 0 1px rgba(15,23,42,0.08)",
  },
  optionText: {
    flex: 1,
    minWidth: 0,
  },
  optionCity: {
    fontSize: "0.84rem",
    fontWeight: 600,
    color: "#1e293b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  optionOffset: {
    fontSize: "0.72rem",
    color: "#94a3b8",
    fontWeight: 600,
  },
  optionCheck: {
    fontSize: "1.1rem",
    color: "#ea580c",
    flexShrink: 0,
  },
  emptyState: {
    padding: "28px 16px",
    textAlign: "center",
    color: "#94a3b8",
  },
}));

// Compact, searchable timezone picker - a plain MUI <Select> can't put a
// search box above its option list, so this is a Popover driven by our own
// state instead. Used only from SuperAdminNavbar.js (this control itself
// isn't rendered anywhere a regular store's session would ever mount).
const TimezoneSelect = ({ value, onChange }) => {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = useMemo(
    () => TIMEZONE_OPTIONS.find((opt) => opt.value === value) || TIMEZONE_OPTIONS[0],
    [value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return TIMEZONE_OPTIONS;
    return TIMEZONE_OPTIONS.filter((opt) => {
      const haystack = `${opt.city} ${opt.country} ${opt.value} ${opt.offset} ${opt.search || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const handleOpen = () => {
    setQuery("");
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handlePick = (tz) => {
    onChange(tz);
    setOpen(false);
  };

  return (
    <>
      <Tooltip title="Display timezone for this SuperAdmin panel - converts every date/time shown here (this doesn't affect any store's own POS)" arrow placement="bottom">
        <div
          ref={anchorRef}
          role="button"
          tabIndex={0}
          onClick={handleOpen}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleOpen()}
          className={`${classes.trigger} ${open ? classes.triggerOpen : ""}`}
        >
          <span className={`${classes.triggerFlag} ${flagClass(selected.country)}`} />
          <span>{selected.city.split(",")[0]}</span>
          <ExpandMoreRoundedIcon className={`${classes.triggerChevron} ${open ? classes.triggerChevronOpen : ""}`} />
        </div>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // index.css force-sets .MuiBackdrop-root to z-index 9999 globally
        // (for an unrelated Dialog-vs-floating-slider fix) without a
        // matching bump for a plain Popover's own Paper - only Menu's root
        // gets one there. Without this, the (invisible) backdrop rendered
        // above this Paper, visually burying the whole picker under the
        // page behind it. Matches this file's own documented convention:
        // "individual dialogs can override with higher values via inline
        // styles".
        PaperProps={{ className: classes.paper, style: { zIndex: 10010, marginTop: 8 } }}
      >
        <div className={classes.searchBar}>
          <div className={classes.searchLabel}>
            <PublicOutlinedIcon style={{ fontSize: "0.9rem" }} />
            Display Timezone
          </div>
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search city, country, UTC offset..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: "1.1rem", color: "#94a3b8" }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <CloseRoundedIcon
                    style={{ fontSize: "1rem", color: "#94a3b8", cursor: "pointer" }}
                    onClick={() => setQuery("")}
                  />
                </InputAdornment>
              ) : null,
            }}
          />
        </div>

        <div className={classes.list}>
          {filtered.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="body2">No matching timezone</Typography>
            </Box>
          ) : (
            filtered.map((opt) => {
              const isActive = opt.value === value;
              return (
                <div
                  key={opt.value}
                  className={`${classes.option} ${isActive ? classes.optionActive : ""}`}
                  onClick={() => handlePick(opt.value)}
                  role="option"
                  aria-selected={isActive}
                >
                  <span className={`${classes.optionFlag} ${flagClass(opt.country)}`} />
                  <div className={classes.optionText}>
                    <div className={classes.optionCity}>{opt.city}</div>
                    <div className={classes.optionOffset}>{opt.offset}</div>
                  </div>
                  {isActive && <CheckRoundedIcon className={classes.optionCheck} />}
                </div>
              );
            })
          )}
        </div>
      </Popover>
    </>
  );
};

export default TimezoneSelect;
